import { Button } from "@/components/builder/button";
import { BuilderImageFit, BuilderImageFocalPoint, toCssImagePosition } from "@/components/builder/builder-image";
import { Typography } from "@/components/builder/typography";
import { LeftArrow } from "@/components/icons/left-arrow-icon";
import RightArrow from "@/components/icons/right-arrow-icon";
import getBuilderLocalizedValue from "@/i18n/get-builder-localized-value";
import { useLocale, useTranslations } from "@/libs/base/components/locale-provider";
import { cn } from "@/libs/base/utils/layout";
import { withChildren } from "@builder.io/react";
import { ReactNode } from "react";
import Carousel, { ButtonGroupProps } from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

interface ProductCarouselProps {
  builderBlock: any;

  title: string | BuilderLocalizedValue;
  buttonShape: "Rounded" | "Rectangle";
  openInNewTab?: boolean;
  description: string | BuilderLocalizedValue;
  showDescriptionOnMobile: boolean;
  viewMoreText?: string | BuilderLocalizedValue;
  viewMoreLink?: string;

  draggable: boolean;
  swipeable: boolean;
  pauseOnHover: boolean;
  autoPlay: boolean;
  autoPlaySpeed?: number;
  infinite: boolean;
  slidesToSlide: number;

  children: ReactNode;
}

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 2160 },
    items: 3,
    partialVisibilityGutter: 20,
  },
  desktop: {
    breakpoint: { max: 2160, min: 1280 },
    items: 3,
    partialVisibilityGutter: 20,
  },
  tablet: {
    breakpoint: { max: 1280, min: 640 },
    items: 2,
    partialVisibilityGutter: 20,
  },
  mobile: {
    breakpoint: { max: 640, min: 0 },
    items: 1,
    partialVisibilityGutter: 0,
  },
};

export const ProductCarousel = withChildren(function ProductCarousel(props: ProductCarouselProps) {
  const locale = useLocale();
  const defaultPlayAutoSpeed = 3000;
  return (
    <div className="grid w-full grid-cols-2 items-center justify-between gap-4 p-4 sm:grid-cols-[0.33fr,0.66fr] sm:grid-rows-[min-content,min-content,1fr,min-content] sm:p-9 sm:pr-0">
      <Typography
        className="col-span-2 h-fit sm:col-span-1 sm:row-start-1 sm:justify-self-start"
        text={getBuilderLocalizedValue(props.title, locale)}
        size="Header"
      />
      <Typography
        className={cn(
          "col-span-2 h-fit sm:col-span-1 sm:row-start-2 sm:justify-self-start",
          !props.showDescriptionOnMobile && "hidden sm:block",
        )}
        text={getBuilderLocalizedValue(props.description, locale)}
        size="Subtitle"
      />
      {props.viewMoreText && props.viewMoreLink && (
        <Button
          href={props.viewMoreLink}
          openInNewTab={props.openInNewTab}
          variant="Filled"
          background="Black"
          shape={props.buttonShape}
          className="mb-[1.25rem] mt-[0,5rem] w-fit sm:col-start-1 sm:row-start-3 sm:my-0 sm:self-start sm:justify-self-start"
          text={getBuilderLocalizedValue(props.viewMoreText, locale)}
        />
      )}

      <Carousel
        className="col-span-2 sm:col-span-1 sm:col-start-2 sm:row-span-4 sm:row-start-1"
        responsive={responsive}
        partialVisible
        ssr
        showDots={false}
        arrows={false}
        swipeable={props.swipeable}
        draggable={props.draggable}
        keyBoardControl
        renderDotsOutside={false}
        renderButtonGroupOutside
        customButtonGroup={<ProductCarouselButtons />}
        additionalTransfrom={0}
        slidesToSlide={props.slidesToSlide}
        autoPlay={props.autoPlay}
        autoPlaySpeed={props.autoPlaySpeed ?? defaultPlayAutoSpeed}
        infinite={props.infinite}
        rewind={false}
        rewindWithAnimation={false}
        pauseOnHover={props.pauseOnHover}
      >
        {props.children ? props.children : <></>}
      </Carousel>
    </div>
  );
});

function ProductCarouselButtons({ next, previous }: ButtonGroupProps) {
  const {
    builder: { hero_carousel: translations },
  } = useTranslations();
  return (
    <div className="col-start-2 col-end-2 row-start-2 row-end-2 mb-[1.25rem] mt-[0,5rem] flex items-center justify-end gap-x-4 sm:col-span-1 sm:col-start-1 sm:col-end-1 sm:row-start-4 sm:row-end-4 sm:my-0 sm:justify-start sm:gap-x-8 sm:justify-self-start">
      <button
        type="button"
        className="fill-black text-black transition disabled:opacity-0"
        onClick={() => previous?.()}
        aria-label={translations.previous_item}
      >
        <LeftArrow className="h-9 w-9" strokeWidth={1} />
      </button>
      <button
        type="button"
        className="fill-black text-black transition disabled:opacity-0"
        onClick={() => next?.()}
        aria-label={translations.next_item}
      >
        <RightArrow className="h-9 w-9" strokeWidth={1} />
      </button>
    </div>
  );
}

export function ProductCarouselItem({
  image,
  title,
  description,
  link,
  linkText,
  imagePosition,
  imageFit,
}: {
  image: string;
  title: string | BuilderLocalizedValue;
  description: string | BuilderLocalizedValue;
  link?: string;
  linkText?: string | BuilderLocalizedValue;
  imagePosition?: BuilderImageFocalPoint;
  imageFit?: BuilderImageFit;
}) {
  const locale = useLocale();
  const altText = getBuilderLocalizedValue(title, locale) || "Product image";
  const contain = imageFit === "Contain";

  return (
    <div className="sm:px-2">
      <img
        src={image}
        alt={altText}
        loading="lazy"
        className={cn(
          "aspect-[4/5] w-full select-none bg-center bg-no-repeat",
          contain ? "object-contain bg-lightgray-100" : "object-cover",
        )}
        style={{ objectPosition: toCssImagePosition(imagePosition) }}
        draggable={false}
      />

      <div className="mt-3 flex items-center justify-between gap-2">
        <Typography text={getBuilderLocalizedValue(title, locale)} size="Headline" variant="Normal" />
        {link && linkText && (
          <Button
            text={getBuilderLocalizedValue(linkText, locale)}
            href={link}
            shape="Rounded"
            background="Black"
            variant="Outline"
            size="Small"
            className="bg-white/30 text-xs"
          />
        )}
      </div>
      <p className="mt-2.5">{getBuilderLocalizedValue(description, locale)}</p>
    </div>
  );
}
